// Peças visuais compartilhadas da tela de carteira: tooltip dos gráficos,
// stat tiles, chip de status e o cartão que embrulha cada gráfico.

import { ReactNode } from 'react';
import { useTheme } from '@/contexts/theme-context';
import { obterVizTheme } from './carteira-viz';
import { STATUS_META, StatusPi, moedaExata } from './carteira-types';

export interface LinhaTooltip {
  nome: string;
  valor: string;
  cor?: string;
}

export function CaixaTooltip({ titulo, linhas }: { titulo: string; linhas: LinhaTooltip[] }) {
  const { theme } = useTheme();
  const viz = obterVizTheme(theme);
  return (
    <div
      className="rounded-md border px-3 py-2 shadow-lg text-xs"
      style={{ background: viz.tooltipFundo, borderColor: viz.tooltipBorda }}
    >
      <p className="font-semibold mb-1.5" style={{ color: viz.textoPrimario }}>{titulo}</p>
      {linhas.map((linha) => (
        <div key={linha.nome} className="flex items-center gap-2 py-0.5">
          {linha.cor && <span className="w-3 h-0.5 rounded-full shrink-0" style={{ background: linha.cor }} />}
          {/* Valor primeiro e forte: quem passa o mouse já sabe a série, quer o número. */}
          <span className="font-semibold tabular-nums" style={{ color: viz.textoPrimario }}>{linha.valor}</span>
          <span style={{ color: viz.textoSecundario }}>{linha.nome}</span>
        </div>
      ))}
    </div>
  );
}

/** Número de destaque. `tom` só pinta o valor quando ele carrega um alerta. */
export function StatTile({
  rotulo, valor, detalhe, tom = 'neutro', icone,
}: {
  rotulo: string;
  valor: string;
  detalhe?: ReactNode;
  tom?: 'neutro' | 'alerta';
  icone?: ReactNode;
}) {
  return (
    <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow p-4 border border-rg-line dark:border-schmersal-gray-dark">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light uppercase tracking-wide">
          {rotulo}
        </p>
        {icone}
      </div>
      <p
        className={`mt-1.5 text-2xl font-bold leading-tight ${
          tom === 'alerta'
            ? 'text-schmersal-blue dark:text-white'
            : 'text-schmersal-gray-dark dark:text-white'
        }`}
      >
        {valor}
      </p>
      {detalhe && (
        <p className="mt-1 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">{detalhe}</p>
      )}
    </div>
  );
}

/**
 * Status sempre com ícone + texto. A cor é reforço, nunca a única informação —
 * "atrasado" e "parado" ficam perto demais no espectro para o contrário.
 */
export function ChipStatus({ status, compacto = false }: { status: StatusPi; compacto?: boolean }) {
  const meta = STATUS_META[status];
  const Icone = meta.icone;
  return (
    <span
      title={meta.descricao}
      className={`inline-flex items-center gap-1.5 rounded-full border font-medium whitespace-nowrap ${
        compacto ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs'
      } text-schmersal-gray-dark dark:text-white`}
      style={{ borderColor: meta.cor, background: `${meta.cor}1A` }}
    >
      <Icone className="h-3.5 w-3.5 shrink-0" style={{ color: meta.cor }} aria-hidden />
      {meta.rotulo}
    </span>
  );
}

export function CartaoGrafico({
  titulo, subtitulo, acao, children,
}: {
  titulo: string;
  subtitulo?: string;
  acao?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow p-5 border border-rg-line dark:border-schmersal-gray-dark">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-semibold text-schmersal-gray-dark dark:text-white">{titulo}</h2>
          {subtitulo && (
            <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mt-0.5">{subtitulo}</p>
          )}
        </div>
        {acao}
      </div>
      {children}
    </div>
  );
}

/** Legenda de séries — presente sempre que houver 2 ou mais. */
export function Legenda({ itens }: { itens: { nome: string; cor: string }[] }) {
  return (
    <div className="flex flex-wrap items-center gap-4 mb-3">
      {itens.map((i) => (
        <span
          key={i.nome}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-schmersal-gray-dark dark:text-white"
        >
          <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: i.cor }} />
          {i.nome}
        </span>
      ))}
    </div>
  );
}

/**
 * Tabela equivalente ao gráfico — obrigatória onde a cor não alcança 3:1 contra
 * a superfície (o amarelo no tema claro).
 */
export function TabelaEquivalente({
  colunas, linhas,
}: {
  colunas: string[];
  linhas: (string | number)[][];
}) {
  return (
    <details className="mt-3 group">
      <summary className="cursor-pointer text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light hover:text-schmersal-blue dark:hover:text-white">
        Ver os números em tabela
      </summary>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
              {colunas.map((c, i) => (
                <th
                  key={c}
                  className={`py-1.5 font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light ${
                    i === 0 ? 'text-left' : 'text-right'
                  }`}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {linhas.map((linha) => (
              <tr key={String(linha[0])} className="border-b border-schmersal-gray-light/50 dark:border-schmersal-gray-dark/50">
                {linha.map((celula, i) => (
                  <td
                    key={i}
                    className={`py-1.5 text-schmersal-gray-dark dark:text-white ${
                      i === 0 ? 'text-left' : 'text-right tabular-nums'
                    }`}
                  >
                    {typeof celula === 'number' ? moedaExata(celula) : celula}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
